import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import defaultTheme from 'ra-ui-materialui/lib/defaultTheme';
import { Notification } from 'ra-ui-materialui/lib/layout';
import { useLogin, useNotify, useRedirect, useTranslate } from 'react-admin';
import classnames from 'classnames';
import { Avatar, Card } from '@material-ui/core';
import { Lock as LockIcon } from '@material-ui/icons';
import { forceChangePassword, redirectOnLogin } from '../amplifyHandlers';
import { isNewPasswordRequired } from '../util/validation';
import DefaultLoginForm from './Login';
import { pageStyles } from './styles';
import ChangePasswordForm from './ChangePassword';

const sanitizeRestProps = ({
  classes,
  className,
  location,
  title,
  array,
  theme,
  dispatch,
  staticContext,
  hasRegistration,
  registrationPathname,
  ...rest
}) => rest;

const Login = ({
  classes,
  className,
  overridePasswordValidation,
  loginFormStyle,
  passwordFormStyle,
  authIcon,
  ...rest
}) => {
  const [step, setStep] = useState('login');
  const [cognitoUser, setCognitoUser] = useState(null);
  const raLogin = useLogin();
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();

  const handleChangePassword = (auth, setLoading) => {
    setLoading(true);
    return forceChangePassword({
      cognitoUser,
      // ...options, // Pass in additional funcs
      ...auth,
    })
      .then(user => {
        redirectOnLogin({
          user,
          redirect,
          errorMsg: translate('error.auth.passwdChangeUnsuccessful'),
        });
      })
      .catch(_ => {
        setLoading(false);
        return notify(
          translate('error.auth.passwdChangeUnsuccessful'),
          'warning'
        );
      });
  };

  const handleLogin = async (auth, setLoading) => {
    setLoading(true);
    return raLogin({ ...auth })
      .then(user => {
        if (window.location.pathname === '/login') {
          return redirectOnLogin({
            user,
            redirect,
          });
        }
      })
      .catch(error => {
        setLoading(false);
        // Check that the error isn't due to new password required
        if (isNewPasswordRequired(error)) {
          setCognitoUser(error);
          setStep('change_password');
          return;
        }

        return notify(
          translate(`${error.message || error}`, {
            _:
              error.message === 'error.auth.invalidCredentials'
                ? 'The email and password combination is not recognised. Please re-enter and try again.'
                : 'Unable to login. Please try again',
          }),
          'warning'
        );
      });
  };

  return (
	<div
		className={classnames(classes.main, className)}
		{...sanitizeRestProps(rest)}
	>
		<Card className={classes.card}>
			<div className={classes.avatar}>
				<Avatar className={classes.icon}>{authIcon || <LockIcon/>}</Avatar>
			</div>
			{step === 'login' ? (
				<DefaultLoginForm
					requestLogin={handleLogin}
					classes={loginFormStyle}
					{...rest}
          />
        ) : (step === 'change_password' ? (
	<ChangePasswordForm
		classes={passwordFormStyle}
		requestChangePassword={handleChangePassword}
		overridePasswordValidation={overridePasswordValidation}
          />
        ) : null)}
		</Card>
		<Notification/>
	</div>
  );
};

Login.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object,
  passwordFormStyle: PropTypes.object,
  loginFormStyle: PropTypes.object,
  meta: PropTypes.object,
  theme: PropTypes.object,
  overridePasswordValidation: PropTypes.func,
  authIcon: PropTypes.object,
  hasRegistration: PropTypes.bool,
  registrationPathname: PropTypes.string,
};

Login.defaultProps = {
  className: null,
  classes: null,
  passwordFormStyle: null,
  loginFormStyle: null,
  meta: null,
  theme: defaultTheme,
  overridePasswordValidation: null,
  authIcon: null,
  hasRegistration: false,
  registrationPathname: '/register',
};

export default withStyles(pageStyles)(Login);
